import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { tokenEventManager } from '../utils/tokenEvents';

const POLLING_INTERVAL = 3000;

const ECOSYSTEM_SOURCE_MAP = {
  QT: 'question',
  DT: 'discussion',
  TT: 'trust',
  IT: 'invitation',
  XT: 'execution',
  CT: 'contribution',
  RT: 'review',
  ET: 'resonance',
};

export const useMiningNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastNotificationIdRef = useRef(0);
  const pollingIntervalRef = useRef(null);

  const fetchUnreadNotifications = async (isPolling = false) => {
    try {
      if (!isPolling) {
        setLoading(true);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get('/api/mining/notifications/unread', config);

      if (response.data.success) {
        const newNotifications = response.data.notifications || [];

        if (isPolling && newNotifications.length > 0) {
          const latestId = lastNotificationIdRef.current;
          const trulyNewNotifications = newNotifications.filter(
            (n) => n.notification_id > latestId
          );

          if (trulyNewNotifications.length > 0) {
            trulyNewNotifications.forEach((notification) => {
              const tokenType = notification.action_context?.token_type;
              const actionType = notification.action_context?.action_type;
              const amount = parseFloat(notification.calculated_amount);

              const source = actionType === 'daily_fermentation' 
                ? 'mining' 
                : ECOSYSTEM_SOURCE_MAP[tokenType] || 'mining';

              tokenEventManager.triggerMiningEffect(
                'SHINKETSU',
                amount,
                source,
                false
              );
            });

            setNotifications((prev) => [...trulyNewNotifications, ...prev]);
            setUnreadCount((prev) => prev + trulyNewNotifications.length);

            const maxId = Math.max(
              ...trulyNewNotifications.map((n) => n.notification_id)
            );
            if (maxId > lastNotificationIdRef.current) {
              lastNotificationIdRef.current = maxId;
            }
          }
        } else if (!isPolling) {
          setNotifications(newNotifications);
          setUnreadCount(newNotifications.length);

          if (newNotifications.length > 0) {
            const maxId = Math.max(
              ...newNotifications.map((n) => n.notification_id)
            );
            lastNotificationIdRef.current = maxId;
          }
        }
      }
    } catch (error) {
      if (!isPolling) {
        console.error('[MiningNotifications] Error fetching notifications:', error);
      }
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        `/api/mining/notifications/${notificationId}/mark-read`,
        {},
        config
      );

      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId)
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[MiningNotifications] Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put('/api/mining/notifications/mark-all-read', {}, config);

      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('[MiningNotifications] Error marking all as read:', error);
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      return;
    }

    fetchUnreadNotifications(false);

    pollingIntervalRef.current = setInterval(() => {
      if (!document.hidden) {
        fetchUnreadNotifications(true);
      }
    }, POLLING_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: () => fetchUnreadNotifications(false),
  };
};
